import { Typography, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Phone, CheckCircle, Timeline, Verified } from "@mui/icons-material";
import theme from "theme";

const HowItWorks = () => {
  const { t } = useTranslation();
  const processSteps = [
    {
      step: "01",
      title: t("consultation"),
      description: t("consultationDetails"),
      icon: <Phone sx={{ fontSize: 32 }} />,
    },
    {
      step: "02",
      title: t("confirmations"),
      description: t("confirmationsDetails"),
      icon: <CheckCircle sx={{ fontSize: 32 }} />,
    },
    {
      step: "03",
      title: t("customPlanning"),
      description: t("customPlanningDetails"),
      icon: <Timeline sx={{ fontSize: 32 }} />,
    },
    {
      step: "04",
      title: t("execution"),
      description: t("executionDetails"),
      icon: <Verified sx={{ fontSize: 32 }} />,
    },
  ];

  return (
    <>
      <Box sx={{ textAlign: "center", mb: { xs: 8, md: 10 } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 3 }}>
          <Box sx={{ width: 60, height: 2, bgcolor: "#FFCD00", mr: 2 }} />
          <Box
            sx={{
              bgcolor: "rgba(82, 10, 118, 0.1)",
              color: theme.palette.secondary.contrastText,
              px: 3,
              py: 1,
              borderRadius: 5,
              fontWeight: 600,
              fontSize: "0.9rem",
            }}
          >
            {t("howItWork")}
          </Box>
          <Box sx={{ width: 60, height: 2, bgcolor: "#FFCD00", ml: 2 }} />
        </Box>

        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: "2rem", sm: "2.5rem", md: "2.5rem" },
            color: theme.palette.secondary.contrastText,
            mb: 4,
            fontWeight: 800,
            lineHeight: 1.2,
          }}
        >
          {t("simpleBookingProcess")}
        </Typography>

        <Typography
          variant="h6"
          sx={{
            color: "text.secondary",
            fontWeight: 400,
            lineHeight: 1.7,
            fontSize: "1rem",
            maxWidth: 700,
            mx: "auto",
            px: 2,
          }}
        >
          {t("worksDetails")}
        </Typography>
      </Box>

      {/* Optimized Process Steps */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 4,
          px: { xs: 2, sm: 4 },
          mb: 4,
          "& > *": {
            flex: { xs: "0 0 100%", sm: "0 0 calc(50% - 32px)", md: "0 0 calc(25% - 32px)" },
            maxWidth: { xs: "100%", sm: "calc(50% - 32px)", md: "calc(25% - 32px)" },
            minWidth: 270,
          },
        }}
      >
        {processSteps.map((step, index) => (
          <Box
            key={step.step}
            sx={{
              position: "relative",
              textAlign: "center",
              zIndex: 1,
              mx: "auto",
              width: "100%",
            }}
          >
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                bgcolor: "white",
                border: "4px solid #FFCD00",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 3,
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  color: theme.palette.secondary.contrastText,
                  fontWeight: 800,
                  fontSize: "1.5rem",
                }}
              >
                {step.step}
              </Typography>
            </Box>

            {/* Step Card */}
            <Box
              sx={{
                bgcolor: "white",
                p: "26px",
                borderRadius: "25px",
                height: 180,
                border: "2px solid rgba(82, 10, 118, 0.1)",
                boxShadow: "0 10px 20px rgba(82, 10, 118, 0.1)",
                transition: "transform 0.25s cubic-bezier(0.17, 0.67, 0.35, 1.1)",
                willChange: "transform",
                "&:hover": {
                  transform: "translateY(-8px) translateZ(0)",
                  "& .step-icon": {
                    color: "#FFCD00",
                  },
                },
              }}
            >
              <Box
                className="step-icon"
                sx={{
                  color: theme.palette.secondary.contrastText,
                  mb: 3,
                  transition: "color 0.25s ease-out",
                  position: "relative",
                  zIndex: 1,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                {step.icon}
              </Box>

              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.secondary.contrastText,
                    mb: 2,
                    fontSize: "16px",
                    lineHeight: 1.3,
                  }}
                >
                  {step.title}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: "text.secondary",
                    lineHeight: 1.6,
                    fontSize: "13.5px",
                  }}
                >
                  {step.description}
                </Typography>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </>
  );
};

export default HowItWorks;